import { useState, useEffect, useRef } from 'react';
import { useWidget } from '../WidgetProvider';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Calendar,
  MessageSquare,
  RefreshCw,
  Download,
  Share2,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Loader2,
  Mail,
  AlertCircle,
  Phone,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ProgressBar } from '../social-proof';

const LOADING_MESSAGES = [
  'Creating your renovation design...',
  'Applying your style preferences...',
  'Rendering the final image...',
  'Adding finishing touches...',
  'Almost there...',
];

export function ResultsStep() {
  const {
    state,
    requestRevision,
    tenantConfig,
    error: widgetError,
    trackEvent,
    generationStatus,
    generationError,
    retryGeneration,
    triggerGeneration,
    requestCallback,
  } = useWidget();

  const [isDownloading, setIsDownloading] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [hasAutoRetried, setHasAutoRetried] = useState(false);
  const [showEmailFallback, setShowEmailFallback] = useState(false);
  const [isRequestingCallback, setIsRequestingCallback] = useState(false);
  const [callbackRequested, setCallbackRequested] = useState(false);
  const autoRetryRef = useRef(false);

  // If we arrive at results without a generated image and generation hasn't started,
  // kick it off (handles edge cases like session resume) - but only if hasCredits
  useEffect(() => {
    if (!state.generatedImage && generationStatus === 'idle' && state.hasCredits) {
      triggerGeneration();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-retry once on generation error
  useEffect(() => {
    if (generationStatus === 'error' && !autoRetryRef.current) {
      autoRetryRef.current = true;
      setHasAutoRetried(true);
      // Brief delay before auto-retry
      const timer = setTimeout(() => {
        retryGeneration();
      }, 2000);
      return () => clearTimeout(timer);
    }

    // If error persists after auto-retry, show email fallback
    if (generationStatus === 'error' && autoRetryRef.current) {
      setShowEmailFallback(true);
    }
  }, [generationStatus, retryGeneration]);

  // Cycle through loading messages
  useEffect(() => {
    if (generationStatus !== 'generating') return;

    const interval = setInterval(() => {
      setLoadingMessageIndex((prev) =>
        prev < LOADING_MESSAGES.length - 1 ? prev + 1 : prev
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [generationStatus]);

  const handleDownload = async () => {
    if (!state.generatedImage) return;

    setIsDownloading(true);
    try {
      // Use proxy to bypass CORS restrictions on Firebase Storage
      const proxyUrl = `/api/images/proxy?url=${encodeURIComponent(state.generatedImage)}`;
      const response = await fetch(proxyUrl);

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `renovation-preview-${state.roomType || 'design'}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      trackEvent('image_downloaded');
    } catch (err) {
      console.error('Download failed:', err);
      // Fallback: open image in new tab
      window.open(state.generatedImage, '_blank');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleShare = async () => {
    if (!state.generatedImage) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Renovation Preview',
          text: 'Check out my AI-generated renovation design!',
          url: state.generatedImage,
        });
      } catch {
        // User cancelled or share failed
      }
    }
  };

  const handleBookCall = () => {
    const url = tenantConfig?.widget?.cta_book_call_url;
    if (url) {
      trackEvent('cta_clicked', { cta_type: 'book_call' });
      window.open(url, '_blank');
    }
  };

  const handleGetQuote = () => {
    const url = tenantConfig?.widget?.cta_get_quote_url;
    if (url) {
      trackEvent('cta_clicked', { cta_type: 'get_quote' });
      window.open(url, '_blank');
    }
  };

  const handleRevision = () => {
    trackEvent('revision_requested');
    requestRevision();
  };

  const handleManualRetry = () => {
    autoRetryRef.current = false;
    setShowEmailFallback(false);
    retryGeneration();
  };

  const handleRequestCallback = async () => {
    setIsRequestingCallback(true);
    try {
      await requestCallback();
      setCallbackRequested(true);
      trackEvent('callback_requested');
    } catch (err) {
      console.error('Failed to request callback:', err);
    } finally {
      setIsRequestingCallback(false);
    }
  };

  const canRevise = state.revisionCount < 1;
  const hasCredits = state.hasCredits;
  const isLoading = generationStatus === 'generating' || (generationStatus === 'idle' && !state.generatedImage && hasCredits);
  const isError = generationStatus === 'error';
  const isReady = generationStatus === 'complete' || !!state.generatedImage;

  // No credits state - simple thank you
  if (hasCredits === false) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4 space-y-6">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>

        <div>
          <h2 className="text-lg font-semibold">Thank you for your submission!</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-sm">
            We've received your renovation request and will be in touch shortly to discuss your project.
          </p>
        </div>

        <Card className="p-4 w-full max-w-sm">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
            <div className="text-left">
              <p className="font-medium text-sm">Your information has been saved</p>
              <p className="text-xs text-muted-foreground">
                {tenantConfig?.company_name || 'We'} will contact you about your {state.roomType?.replace('_', ' ')} renovation.
              </p>
            </div>
          </div>
        </Card>

        {/* CTA Buttons */}
        <div className="w-full max-w-sm space-y-3">
          {tenantConfig?.widget?.cta_book_call_url && (
            <Button className="w-full" size="lg" onClick={handleBookCall}>
              <Calendar className="mr-2 h-4 w-4" />
              Book a Free Consultation
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}

          {tenantConfig?.widget?.cta_get_quote_url && (
            <Button variant="outline" className="w-full" size="lg" onClick={handleGetQuote}>
              <MessageSquare className="mr-2 h-4 w-4" />
              Get a Detailed Quote
            </Button>
          )}
        </div>

        {tenantConfig?.widget?.business_phone && (
          <div className="text-sm text-muted-foreground">
            <p>Questions? Call us at</p>
            <a
              href={`tel:${tenantConfig.widget.business_phone}`}
              className="font-medium text-primary hover:underline"
            >
              {tenantConfig.widget.business_phone}
            </a>
          </div>
        )}
      </div>
    );
  }

  // Loading state - generation in progress
  if (isLoading && hasCredits) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4">
        <div className="relative mb-8">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-10 h-10 text-primary animate-pulse" />
          </div>
          <div className="absolute inset-0 -m-2">
            <Loader2 className="w-24 h-24 text-primary/30 animate-spin" />
          </div>
        </div>

        <h2 className="text-lg font-semibold">Creating your renovation preview</h2>
        <p className="text-sm text-muted-foreground mt-2 h-6 transition-all">
          {LOADING_MESSAGES[loadingMessageIndex]}
        </p>

        <div className="flex gap-1.5 mt-6">
          {LOADING_MESSAGES.map((_, index) => (
            <div
              key={index}
              className={cn(
                'w-2 h-2 rounded-full transition-all duration-300',
                index <= loadingMessageIndex ? 'bg-primary' : 'bg-muted'
              )}
            />
          ))}
        </div>

        <div className="w-full max-w-sm mt-6">
          <ProgressBar durationMs={60000} isComplete={false} />
        </div>

        <p className="text-xs text-muted-foreground mt-8 max-w-xs">
          Our AI is generating your custom renovation visualization.
          This usually takes 30-60 seconds.
        </p>
      </div>
    );
  }

  // Error state with email fallback
  if (isError && showEmailFallback) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4 space-y-6">
        <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center">
          <Mail className="w-8 h-8 text-accent" />
        </div>

        <div>
          <h2 className="text-lg font-semibold">Your design is being prepared</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-sm">
            We're still working on your renovation preview. We'll email it to{' '}
            <span className="font-medium text-foreground">{state.contact.email}</span>{' '}
            as soon as it's ready.
          </p>
        </div>

        <Card className="p-4 w-full max-w-sm">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
            <div className="text-left">
              <p className="font-medium text-sm">Your information has been saved</p>
              <p className="text-xs text-muted-foreground">
                {tenantConfig?.company_name || 'The contractor'} will be in touch about your project.
              </p>
            </div>
          </div>
        </Card>

        <Button variant="outline" size="sm" onClick={handleManualRetry}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Try Again
        </Button>

        {/* CTA Buttons */}
        <div className="w-full max-w-sm space-y-3">
          {tenantConfig?.widget?.cta_book_call_url && (
            <Button className="w-full" size="lg" onClick={handleBookCall}>
              <Calendar className="mr-2 h-4 w-4" />
              Book a Free Consultation
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}

          {tenantConfig?.widget?.cta_get_quote_url && (
            <Button variant="outline" className="w-full" size="lg" onClick={handleGetQuote}>
              <MessageSquare className="mr-2 h-4 w-4" />
              Get a Detailed Quote
            </Button>
          )}
        </div>

        {tenantConfig?.widget?.business_phone && (
          <div className="text-sm text-muted-foreground">
            <p>Questions? Call us at</p>
            <a
              href={`tel:${tenantConfig.widget.business_phone}`}
              className="font-medium text-primary hover:underline"
            >
              {tenantConfig.widget.business_phone}
            </a>
          </div>
        )}
      </div>
    );
  }

  // Error state before email fallback (auto-retrying)
  if (isError && !showEmailFallback) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4">
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-destructive" />
        </div>
        <h2 className="text-lg font-semibold">Retrying...</h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-sm">
          {generationError || 'Something went wrong. Retrying automatically...'}
        </p>
        <Loader2 className="w-6 h-6 text-muted-foreground animate-spin mt-4" />
      </div>
    );
  }

  // Success state - generation complete
  return (
    <div className="space-y-6 pb-8">
      {/* Success Header */}
      <div className="text-center">
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
          <CheckCircle2 className="w-6 h-6 text-green-600" />
        </div>
        <h2 className="text-xl font-semibold">Your renovation preview is ready!</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Here's what your {state.roomType?.replace('_', ' ')} could look like
        </p>
      </div>

      {/* Main Image */}
      <div className="relative rounded-lg overflow-hidden shadow-lg">
        {state.generatedImage ? (
          <img
            src={state.generatedImage}
            alt="AI-generated renovation preview"
            className="w-full aspect-[4/3] object-cover"
          />
        ) : (
          <div className="w-full aspect-[4/3] bg-muted flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-muted-foreground animate-pulse" />
          </div>
        )}

        {/* Image Actions */}
        <div className="absolute bottom-3 right-3 flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            className="bg-white/90 hover:bg-white"
            onClick={handleDownload}
            disabled={isDownloading}
          >
            <Download className="w-4 h-4" />
          </Button>
          {navigator.share && (
            <Button
              size="sm"
              variant="secondary"
              className="bg-white/90 hover:bg-white"
              onClick={handleShare}
            >
              <Share2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* CTA Buttons */}
      <div className="space-y-3">
        {tenantConfig?.widget?.cta_book_call_url && (
          <Button className="w-full" size="lg" onClick={handleBookCall}>
            <Calendar className="mr-2 h-4 w-4" />
            Book a Free Consultation
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        )}

        {tenantConfig?.widget?.cta_get_quote_url && (
          <Button variant="outline" className="w-full" size="lg" onClick={handleGetQuote}>
            <MessageSquare className="mr-2 h-4 w-4" />
            Get a Detailed Quote
          </Button>
        )}
      </div>

      {/* Request Callback Button */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Have questions?</p>
            <p className="text-sm text-muted-foreground">
              {callbackRequested ? 'We will contact you shortly!' : 'Request a callback from our team'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRequestCallback}
            disabled={isRequestingCallback || callbackRequested}
          >
            {isRequestingCallback ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : callbackRequested ? (
              <CheckCircle2 className="mr-2 h-4 w-4" />
            ) : (
              <Phone className="mr-2 h-4 w-4" />
            )}
            {callbackRequested ? 'Requested' : 'Request Callback'}
          </Button>
        </div>
        {widgetError && <p className="text-sm text-destructive mt-2">{widgetError}</p>}
      </Card>

      {/* Analysis Details - only show if there's meaningful content */}
      {state.geminiAnalysis && (
        state.geminiAnalysis.detected_elements?.length > 0 ||
        state.geminiAnalysis.complexity_factors?.length > 0 ||
        state.geminiAnalysis.notes
      ) && (
        <Card className="p-4">
          <h3 className="font-medium mb-3">Room Analysis</h3>
          <div className="space-y-2 text-sm">
            {state.geminiAnalysis.detected_elements?.length > 0 && (
              <div>
                <p className="text-muted-foreground">Detected Elements</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {state.geminiAnalysis.detected_elements.map((element, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {element}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {state.geminiAnalysis.complexity_factors?.length > 0 && (
              <div className="mt-3">
                <p className="text-muted-foreground">Complexity Factors</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {state.geminiAnalysis.complexity_factors.map((factor, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">
                      {factor}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {state.geminiAnalysis.notes && (
              <div className="mt-3">
                <p className="text-muted-foreground">Notes</p>
                <p className="mt-1">{state.geminiAnalysis.notes}</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Business Contact */}
      {tenantConfig?.widget?.business_phone && (
        <div className="text-center text-sm text-muted-foreground">
          <p>Questions? Call us at</p>
          <a
            href={`tel:${tenantConfig.widget.business_phone}`}
            className="font-medium text-primary hover:underline"
          >
            {tenantConfig.widget.business_phone}
          </a>
        </div>
      )}
    </div>
  );
}
